import { Box, Tab, Tabs } from '@mui/material'
import { Link } from 'react-router-dom'
import { type LicensedEstablishment } from 'src/types/api'
import { useCurrentCorporateAccountStore } from 'stores/useCurrentCorporateAccountStore'
import { useSetLe } from 'stores/useSetLe'
import { useEffect } from 'react'
import { ErrorIndicator } from 'components/Shared/ErrorIndicator'
import { ActivityIndicator } from 'components/Shared/ActivityIndicator'
import { NoDataIndicator } from 'components/Shared/NoDataIndicator'
import NoPatronsCheckedInIcon from 'assets/NoPatronsCheckedIn.png'
import { sortComparatorByStandardName } from 'utils/util'
import { getLEIdsByPermission } from 'utils/permissions'
import { SelectLicensedEstablishment } from 'components/Shared/SelectLicensedEstablishment'
import { useSetMe } from 'stores/useMeQuery'

export const GamingReportsTabs = ({
  currentTab,
  locationId,
}: {
  currentTab: string
  locationId: number
}) => {
  const { passedMe } = useSetMe()
  const meQuery = passedMe

  const selectedCorporateAccountId =
    useCurrentCorporateAccountStore().currentCorporateAccountId

  const licensedEstablishments = meQuery.data?.licensedEstablishments?.filter(
    (le) => le.corporateAccountId === selectedCorporateAccountId
  )

  // Get LE Ids for the permission the user has access to
  const leIdsByPermission: number[] = getLEIdsByPermission(
    meQuery.data,
    selectedCorporateAccountId,
    'gamingReportsPermission'
  )

  // Filter data by permission
  const licensedEstablishmentsFiltered = licensedEstablishments?.filter((x) =>
    leIdsByPermission.includes(x.id)
  )

  const sortedLicensedEstablishments = licensedEstablishmentsFiltered
    ? [...licensedEstablishmentsFiltered].sort(sortComparatorByStandardName)
    : []

  const setPassedLe = useSetLe((state) => state.setPassedLe)
  const { passedLe } = useSetLe()

  useEffect(() => {
    setPassedLe(passedLe ?? sortedLicensedEstablishments?.[0])
  }, [meQuery, selectedCorporateAccountId])

  if (meQuery.isPending) {
    return <ActivityIndicator />
  }

  if (meQuery.isError) {
    return <ErrorIndicator />
  }

  const gamingReportsTabs = [
    {
      label: 'Credits Wagered by Machine',
      value: `1`,
      to: `/GamingReports/CreditsWageredByMachine`,
    },
    {
      label: 'Credits Wagered by Hour',
      value: `2`,
      to: `/GamingReports/CreditsWageredByHour`,
    },
    {
      label: 'Credits Wagered: Hourly Trends',
      value: `3`,
      to: `/GamingReports/CreditsWageredHourlyTrends`,
    },
    {
      label: 'Weekly Snapshot',
      value: '',
      to: `/GamingReports/WeeklySnapshotLocationSelect`,
    },
    {
      label: 'NTI Trends',
      value: `4`,
      to: `/GamingReports/TrailingNtiTrends`,
    },
    {
      label: 'VGT Snapshot',
      value: `5`,
      to: `/GamingReports/VgtSnapshot`,
    },
    {
      label: 'VGT Trends',
      value: `6`,
      to: `/GamingReports/VgtTrends`,
    },
    {
      label: 'Performance Trends',
      value: '',
      to: `/GamingReports/PerformanceTrendsTablePage`,
    },
  ]

  if (leIdsByPermission.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          rowGap: '16px',
          borderWidth: 1,
          borderColor: 'grey.300',
          borderRadius: 2,
          px: '16px',
          py: '16px',
          mt: '1px',
        }}
      >
        <NoDataIndicator
          noImage
          noDataMessage="Your current permissions do not allow access to view reports. To obtain access, please contact your Account Manager for assistance."
        />
      </Box>
    )
  }

  if (
    !sortedLicensedEstablishments ||
    sortedLicensedEstablishments.length === 0
  ) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          rowGap: '16px',
          borderWidth: 1,
          borderColor: 'grey.300',
          borderRadius: 2,
          px: '16px',
          py: '16px',
          mt: '1px',
        }}
      >
        <NoDataIndicator
          imageSource={NoPatronsCheckedInIcon}
          noDataMessage="Cannot view reports because there are no locations available for this corporate account"
        />
      </Box>
    )
  }

  return (
    <>
      <SelectLicensedEstablishment
        onSelectLicensedEstablishment={(selectedLe: LicensedEstablishment) => {
          // Update the passed LE
          setPassedLe(selectedLe)
        }}
        locationId={passedLe?.id}
        permission={'gamingReportsPermission'}
      />
      <Tabs
        value={currentTab}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
      >
        {leIdsByPermission.length > 0 &&
          gamingReportsTabs.map((tab) => (
            <Tab
              key={tab.label}
              label={tab.label}
              value={tab.value}
              to={tab.to}
              component={Link}
              disabled={Number.isNaN(locationId) && passedLe === undefined}
            />
          ))}
      </Tabs>
    </>
  )
}
